// src/components/Waitlist/JoinWaitlist.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  User,
  Briefcase,
  Check,
  Sparkles,
  ArrowRight,
  Users,
  ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

const JoinWaitList = () => {
  const navigate = useNavigate();
  const cardRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    field: '',
    recaptchaToken: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [signupCount, setSignupCount] = useState(2847);

  // === ENV URL (MUST BE SET IN .env.local) ===
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // === FALLBACK FOR DEV ONLY (NEVER IN PRODUCTION) ===
  if (!API_BASE_URL) {
    console.warn('REACT_APP_API_URL is not set. Using localhost for dev.');
  }

  // Click outside → go back to home
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        navigate('/', { replace: true });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [navigate]);

  // Industries list
  const industries = useMemo(
    () => [
      { value: '', label: 'Select your industry', disabled: true },
      { value: 'accounting', label: 'Accounting & Tax Services' },
      { value: 'advertising', label: 'Advertising & Marketing' },
      { value: 'consulting', label: 'Business Consulting' },
      { value: 'cybersecurity', label: 'Cybersecurity Services' },
      { value: 'education', label: 'Education & Training' },
      { value: 'elearning', label: 'E-Learning Platforms' },
      { value: 'financial', label: 'Financial Services' },
      { value: 'healthcare', label: 'Healthcare Services' },
      { value: 'home-services', label: 'Home Services (Plumbing, HVAC)' },
      { value: 'hr', label: 'Human Resources' },
      { value: 'insurance', label: 'Insurance Services' },
      { value: 'it-consulting', label: 'IT Consulting' },
      { value: 'legal', label: 'Legal Services' },
      { value: 'logistics', label: 'Logistics & Supply Chain' },
      { value: 'management', label: 'Management Consulting' },
      { value: 'nonprofit', label: 'Non-Profit' },
      { value: 'real-estate', label: 'Real Estate' },
      { value: 'saas', label: 'SaaS/Software' },
      { value: 'senior-care', label: 'Senior Care' },
      { value: 'telecom', label: 'Telecommunications' },
      { value: 'waste-management', label: 'Waste Management' },
      { value: 'wealth-management', label: 'Wealth Management' },
    ],
    []
  );

  const confettiColors = useMemo(
    () => ['#ec4899', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b'],
    []
  );

  // === FETCH STATS ===
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/stats`);
        if (!res.ok) throw new Error('Failed to fetch stats');
        const data = await res.json();
        setSignupCount(data.total || 2847);
      } catch (err) {
        console.error('Stats fetch failed:', err);
      }
    };
    if (API_BASE_URL) fetchStats();
  }, [API_BASE_URL]);

  const handleChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  }, []);

  const handleRecaptcha = useCallback((token) => {
    setFormData((prev) => ({ ...prev, recaptchaToken: token || '' }));
    setError('');
  }, []);

  // === SUBMIT HANDLER ===
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!formData.recaptchaToken) return setError('Please complete reCAPTCHA');
      if (!formData.name || !formData.email || !formData.field)
        return setError('All fields are required');

      setIsLoading(true);
      setError('');

      try {
        const res = await fetch(`${API_BASE_URL}/api/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || `Server error: ${res.status}`);
        }

        setIsSubmitted(true);
        setFormData({ name: '', email: '', field: '', recaptchaToken: '' });
      } catch (err) {
        console.error('Submit error:', err);
        setError(err.message || 'Failed to connect. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [formData, API_BASE_URL]
  );

  const goBack = useCallback(() => navigate('/'), [navigate]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="relative w-full max-w-2xl"
      >
        <div className="relative bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden">
          <AnimatePresence mode="wait">
            {!isSubmitted ? (
              <motion.div key="form" className="p-8 md:p-12">
                {/* Back Button */}
                <motion.button
                  onClick={goBack}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white/70"
                >
                  <ArrowLeft size={16} />
                  <span className="hidden sm:inline text-sm">Back</span>
                </motion.button>

                {/* Header */}
                <div className="text-center mb-10">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30"
                  >
                    <Sparkles size={16} className="text-purple-400" />
                    <span className="text-sm font-medium text-purple-300">Limited Access</span>
                  </motion.div>

                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-4xl md:text-5xl font-black text-white mb-4"
                  >
                    Join the{' '}
                    <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
                      Revolution
                    </span>
                  </motion.h2>

                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-lg text-slate-300"
                  >
                    Be among the first to experience AI-powered lead intelligence.
                  </motion.p>

                  <div className="flex items-center justify-center gap-3 mt-6">
                    <div className="flex -space-x-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 border-2 border-slate-900"
                        />
                      ))}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {signupCount.toLocaleString()} pioneers
                      </p>
                      <p className="text-xs text-slate-400">on the waitlist</p>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  {/* Name */}
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                    <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="John Doe"
                        required
                        disabled={isLoading}
                        className={`w-full pl-10 pr-4 py-3 bg-white/5 border ${
                          focusedField === 'name' ? 'border-purple-500/50' : 'border-white/10'
                        } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50 transition-all`}
                      />
                    </div>
                  </motion.div>

                  {/* Email */}
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                    <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="john@company.com"
                        required
                        disabled={isLoading}
                        className={`w-full pl-10 pr-4 py-3 bg-white/5 border ${
                          focusedField === 'email' ? 'border-purple-500/50' : 'border-white/10'
                        } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50 transition-all`}
                      />
                    </div>
                  </motion.div>

                  {/* Industry */}
                  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                    <label htmlFor="field" className="block text-sm font-semibold text-white mb-2">
                      Industry
                    </label>
                    <div className="relative">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <select
                        id="field"
                        name="field"
                        value={formData.field}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('field')}
                        onBlur={() => setFocusedField(null)}
                        disabled={isLoading}
                        className={`w-full pl-10 pr-10 py-3 bg-white/5 border ${
                          focusedField === 'field' ? 'border-purple-500/50' : 'border-white/10'
                        } rounded-lg text-white focus:outline-none focus:border-purple-500/50 appearance-none cursor-pointer`}
                      >
                        {industries.map((i) => (
                          <option key={i.value} value={i.value} disabled={i.disabled} className="bg-slate-900 text-white">
                            {i.label}
                          </option>
                        ))}
                      </select>
                      <svg
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </motion.div>

                  {/* reCAPTCHA */}
                  <div className="flex justify-center my-6">
                    <ReCAPTCHA
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptcha}
                      size="normal"
                      theme="dark"
                      onError={() => setError('reCAPTCHA failed to load')}
                    />
                  </div>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    disabled={isLoading}
                    whileHover={isLoading ? {} : { scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="group relative w-full py-3 rounded-lg font-bold text-base overflow-hidden disabled:opacity-50"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500" />
                    <span className="relative text-white flex items-center justify-center gap-2">
                      {isLoading ? 'Submitting...' : 'Secure My Spot'}
                      <ArrowRight className={`transition-transform ${isLoading ? '' : 'group-hover:translate-x-1'}`} size={18} />
                    </span>
                  </motion.button>

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="text-red-400 text-sm text-center mt-2"
                        role="alert"
                      >
                        {error}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  <div className="flex items-center justify-center gap-4 text-xs text-slate-400 pt-2">
                    <div className="flex items-center gap-2">
                      <Check size={12} className="text-green-400" />
                      <span>No spam, ever</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check size={12} className="text-green-400" />
                      <span>Unsubscribe anytime</span>
                    </div>
                  </div>
                </form>
              </motion.div>
            ) : (
              /* SUCCESS */
              <motion.div key="success" className="p-8 md:p-12 text-center">
                {/* Confetti */}
                <AnimatePresence>
                  {[...Array(10)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor: confettiColors[i % confettiColors.length],
                        left: '50%',
                        top: '40%',
                      }}
                      initial={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                      animate={{
                        opacity: 0,
                        scale: 0,
                        x: (Math.random() - 0.5) * 200,
                        y: (Math.random() - 0.5) * 200,
                      }}
                      transition={{ duration: 1, delay: i * 0.05 }}
                    />
                  ))}
                </AnimatePresence>

                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 mb-6 mx-auto"
                >
                  <Check size={32} className="text-white" />
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-4xl md:text-5xl font-black text-white mb-4"
                >
                  You're In!
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-lg text-slate-300 mb-6 max-w-md mx-auto"
                >
                  Welcome to the future of lead intelligence.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-3 gap-3 max-w-md mx-auto mb-6"
                >
                  {[
                    { icon: Users, label: 'Position', value: `#${(signupCount + 1).toLocaleString()}` },
                    { icon: Sparkles, label: 'Reward', value: '20% Off' },
                    { icon: Mail, label: 'Status', value: 'Confirmed' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/5 rounded-lg p-3 border border-white/10">
                      <s.icon className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                      <div className="text-xs text-slate-400 mb-1">{s.label}</div>
                      <div className="text-xs font-bold text-white">{s.value}</div>
                    </div>
                  ))}
                </motion.div>

                <motion.button
                  onClick={goBack}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 rounded-lg font-semibold text-base bg-white/10 hover:bg-white/20 border border-white/20 text-white"
                >
                  Continue Exploring
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default JoinWaitList;